import type { Doc } from "building-types/doc";
import type { FloorSplitData } from "building-types/floors/floorSplitDataTypes";
import type { ComponentMemberEntityHandle } from "building-types/handles/docEntityHandleTypes";
import { assert } from "common/utils/assertOfType";
import { mergeIn } from "common/utils/objectUtils";
import { docGetRoomIdsForLevel } from "doc/elements/docGetBlocks";
import { getFloorSplitData, isHorizontalFloorAssemblyRoomDerived } from "./elements/floor/floorAssembly";

/**
 * When rooms or floor systems are added to a component separately, drop room
 * references on room-derived floors unless both the floor system and the room
 * are in the same add batch. Applies to explicit `roomIds` / `excludedRoomIds`
 * and to `allRoomsOnLevel` floors (which resolve rooms dynamically from the level).
 */
export function docRemapFloorRoomRefsForComponent(doc: Doc, addedHandles: readonly ComponentMemberEntityHandle[]): Doc {
  const batchRoomIds = new Set<string>();
  const batchFloorSystemIds = new Set<string>();

  for (const h of addedHandles) {
    switch (h.type) {
      case "ROOM":
        batchRoomIds.add(h.id);
        break;
      case "FLOOR_SYSTEM":
        batchFloorSystemIds.add(h.id);
        break;
    }
  }

  if (batchRoomIds.size === 0 && batchFloorSystemIds.size === 0) {
    return doc;
  }

  for (const floorId in doc.floors) {
    const floor = doc.floors[floorId];
    if (!floor || !isHorizontalFloorAssemblyRoomDerived(floor)) {
      continue;
    }

    const floorSystemInBatch = batchFloorSystemIds.has(floor.parentId);

    if (floor.allRoomsOnLevel) {
      const levelRoomIds = docGetRoomIdsForLevel(doc, floor.levelId);
      const currentExcluded = new Set(floor.excludedRoomIds ?? []);
      const currentEffective = levelRoomIds.filter((id) => !currentExcluded.has(id));

      if (batchRoomIds.size === 0) {
        if (floorSystemInBatch) {
          doc = clearRoomDerivedFloorRefs(doc, floorId);
        }
        continue;
      }

      const targetEffective = floorSystemInBatch
        ? currentEffective.filter((id) => batchRoomIds.has(id))
        : currentEffective.filter((id) => !batchRoomIds.has(id));

      doc = applyRoomDerivedFloorEffectiveRooms(doc, floorId, targetEffective, levelRoomIds);
      continue;
    }

    const currentRoomIds = floor.roomIds ?? [];
    const currentExcludedIds = floor.excludedRoomIds ?? [];
    if (currentRoomIds.length === 0 && currentExcludedIds.length === 0) {
      continue;
    }

    const keepRef = (roomId: string) => keepRoomRef(roomId, batchRoomIds, floorSystemInBatch);
    const remainingRoomIds = currentRoomIds.filter(keepRef);
    const remainingExcludedIds = currentExcludedIds.filter(keepRef);

    if (remainingRoomIds.length !== currentRoomIds.length) {
      doc = docSetFloorRoomIds(doc, floorId, remainingRoomIds);
    }
    if (remainingExcludedIds.length !== currentExcludedIds.length) {
      doc = mergeIn(doc, ["floors", floorId], { excludedRoomIds: remainingExcludedIds });
    }
  }

  return doc;
}

function keepRoomRef(roomId: string, batchRoomIds: Set<string>, floorSystemInBatch: boolean): boolean {
  return batchRoomIds.has(roomId) === floorSystemInBatch;
}

function clearRoomDerivedFloorRefs(doc: Doc, floorId: string): Doc {
  return mergeIn(doc, ["floors", floorId], {
    allRoomsOnLevel: undefined,
    roomIds: [],
    excludedRoomIds: undefined,
    splitData: undefined,
  });
}

function splitDataPatchForRemappedRooms(
  doc: Doc,
  floorId: string,
): { splitData?: undefined } | { splitData: Pick<FloorSplitData, "splitLines" | "bays"> } {
  const floor = doc.floors[floorId];
  const existingSplitData = floor ? getFloorSplitData(floor) : undefined;
  if (existingSplitData === undefined) {
    return {};
  }
  return { splitData: { splitLines: existingSplitData.splitLines, bays: {} } };
}

function applyRoomDerivedFloorEffectiveRooms(
  doc: Doc,
  floorId: string,
  effectiveRoomIds: readonly string[],
  levelRoomIds: readonly string[],
): Doc {
  if (effectiveRoomIds.length === 0) {
    return clearRoomDerivedFloorRefs(doc, floorId);
  }

  assert(
    levelRoomIds.length > 0,
    "applyRoomDerivedFloorEffectiveRooms: Non-empty effectiveRoomIds require non-empty levelRoomIds",
  );

  const allLevelRoomsKept =
    effectiveRoomIds.length === levelRoomIds.length && levelRoomIds.every((id) => effectiveRoomIds.includes(id));

  const splitDataPatch = splitDataPatchForRemappedRooms(doc, floorId);

  if (allLevelRoomsKept) {
    return mergeIn(doc, ["floors", floorId], {
      allRoomsOnLevel: true,
      roomIds: undefined,
      excludedRoomIds: [],
      ...splitDataPatch,
    });
  }

  const excludedRoomIds = levelRoomIds.filter((id) => !effectiveRoomIds.includes(id));
  return mergeIn(doc, ["floors", floorId], {
    allRoomsOnLevel: true,
    roomIds: undefined,
    excludedRoomIds,
    ...splitDataPatch,
  });
}

/**
 * Updates a room-derived floor's roomIds. Clears splitData when all rooms are
 * removed; otherwise clears stale bays while preserving split lines.
 *
 * For full bay reconciliation (inspector edits), use the lib implementation in
 * reconcileBaysForRoomOutlines.ts, which can depend on building-generation.
 */
export function docSetFloorRoomIds(doc: Doc, floorId: string, roomIds: readonly string[]): Doc {
  const floor = doc.floors[floorId];
  if (!floor || !isHorizontalFloorAssemblyRoomDerived(floor)) {
    return doc;
  }

  if (roomIds.length === 0) {
    return mergeIn(doc, ["floors", floorId], {
      allRoomsOnLevel: undefined,
      roomIds: [],
      excludedRoomIds: undefined,
      splitData: undefined,
    });
  }

  const existingSplitData = getFloorSplitData(floor);
  if (!existingSplitData) {
    return mergeIn(doc, ["floors", floorId], {
      allRoomsOnLevel: undefined,
      roomIds: [...roomIds],
      excludedRoomIds: undefined,
    });
  }

  return mergeIn(doc, ["floors", floorId], {
    allRoomsOnLevel: undefined,
    roomIds: [...roomIds],
    excludedRoomIds: undefined,
    splitData: { splitLines: existingSplitData.splitLines, bays: {} },
  });
}
